import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SystemInsightsDataService,
  PlatformInsights,
  OrgOverviewRow,
  InsightPeriod,
  INSIGHT_PERIOD_OPTIONS,
  daysSince,
  lastActivityLabel,
  formatCost,
  QUIET_DAYS,
} from '../../services/systemInsightsDataService';

/** A trial this close to its end is worth a look before it lapses. */
const TRIAL_SOON_DAYS = 14;

/** An unpublished post older than this is a stuck review, not a work in progress. */
const STALE_BACKLOG_DAYS = 21;

type SortKey = 'name' | 'children' | 'staff' | 'posts' | 'photos' | 'published' | 'reports' | 'ai' | 'activity';

/**
 * System Admin → Overview. Every organisation on the platform, in one
 * scanning table: how many children and staff, how much was written, and how
 * long since anyone did anything at all.
 *
 * Counts only. Nothing on this page identifies a child, a staff member or a
 * post — the API cannot return that (see systemInsightsDataService).
 *
 * The flags are the point of the page. Sorting by "last activity" answers the
 * question that actually matters commercially — who has gone quiet — which no
 * absolute number does.
 */
const Overview: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<InsightPeriod>('this_week');
  const [data, setData] = useState<PlatformInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('activity');
  const [quietOnly, setQuietOnly] = useState(false);

  const load = (p: InsightPeriod) => {
    setLoading(true);
    setError(null);
    SystemInsightsDataService.fetchPlatform(p)
      .then(setData)
      .catch(err => setError(err?.response?.data?.error || 'Failed to load platform insights.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(period); }, [period]);

  const rows = data?.organisations ?? [];
  const unit = data?.period.unit ?? 'week';

  const quietCount = useMemo(
    () => rows.filter(r => {
      const d = daysSince(r.lastActivityAt);
      return d === null || d >= QUIET_DAYS;
    }).length,
    [rows],
  );

  const visible = useMemo(() => {
    let out = quietOnly
      ? rows.filter(r => { const d = daysSince(r.lastActivityAt); return d === null || d >= QUIET_DAYS; })
      : [...rows];
    const by = (fn: (r: OrgOverviewRow) => number) => (a: OrgOverviewRow, b: OrgOverviewRow) => fn(b) - fn(a);
    switch (sort) {
      case 'name':      out.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'children':  out.sort(by(r => r.children.onRoll)); break;
      case 'staff':     out.sort(by(r => r.staffTotal)); break;
      case 'posts':     out.sort(by(r => r.posts)); break;
      case 'photos':    out.sort(by(r => r.photos)); break;
      case 'published': out.sort(by(r => r.published)); break;
      case 'reports':   out.sort(by(r => r.reports)); break;
      case 'ai':        out.sort(by(r => r.aiCost)); break;
      // Quietest first: the ones needing attention are at the top.
      case 'activity':  out.sort((a, b) => (daysSince(b.lastActivityAt) ?? 1e9) - (daysSince(a.lastActivityAt) ?? 1e9)); break;
    }
    return out;
  }, [rows, sort, quietOnly]);

  // Platform totals — the same figures summed, so the page opens on a scale
  // before it opens on a list.
  const totals = useMemo(() => rows.reduce((acc, r) => ({
    children: acc.children + r.children.onRoll,
    staff: acc.staff + r.staffTotal,
    posts: acc.posts + r.posts,
    photos: acc.photos + r.photos,
    published: acc.published + r.published,
    reports: acc.reports + r.reports,
    ai: acc.ai + r.aiCost,
  }), { children: 0, staff: 0, posts: 0, photos: 0, published: 0, reports: 0, ai: 0 }), [rows]);

  const periodLabel = INSIGHT_PERIOD_OPTIONS.find(o => o.id === period)?.label ?? '';

  const sortable = (key: SortKey, label: string, numeric = true) => (
    <th
      className={numeric ? 'sys-table__num sys-sortable' : 'sys-sortable'}
      aria-sort={sort === key ? 'descending' : 'none'}
    >
      <button type="button" onClick={() => setSort(key)} className="sys-sort-btn">
        {label}
        {sort === key && <i className="fas fa-caret-down ms-1" aria-hidden="true" />}
      </button>
    </th>
  );

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Overview</h1>
        <span className="sys-page__count">
          {loading && !data ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'organisation' : 'organisations'} · ${periodLabel.toLowerCase()}`}
        </span>
      </div>

      <div className="pill-toolbar" role="toolbar" aria-label="Overview period">
        <div className="sys-period" role="group" aria-label="Period">
          {INSIGHT_PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              className="sys-period__btn"
              aria-pressed={period === opt.id}
              onClick={() => setPeriod(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {quietCount > 0 && (
          <button
            type="button"
            className="sys-filter-btn ms-2"
            onClick={() => setQuietOnly(v => !v)}
            aria-pressed={quietOnly}
            title={`Organisations with nothing recorded for ${QUIET_DAYS} days or more`}
          >
            <i className="fas fa-moon" aria-hidden="true" />
            {quietCount} quiet
          </button>
        )}
        <div className="pill-toolbar__spacer" />
        <button
          className="pill-toolbar__icon-btn"
          onClick={() => load(period)}
          disabled={loading}
          title="Reload"
          aria-label="Reload insights"
        >
          <i className="fas fa-sync-alt" style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>

      <div className="sys-page__body">
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && !data ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        ) : (
          // Switching period keeps the previous numbers on screen until the new
          // ones arrive. Without this they sit under the NEW period's label and
          // read as fact — so the whole block is dimmed while it is out of date.
          <div className={loading ? 'sys-stale' : undefined} aria-busy={loading}>
            <div className="sys-stats mb-3">
              <Stat label="Children on roll" value={totals.children.toLocaleString()} icon="fa-child-reaching" />
              <Stat label="Staff" value={totals.staff.toLocaleString()} icon="fa-user-group" />
              <Stat label="Posts" value={totals.posts.toLocaleString()} icon="fa-pen-to-square" sub={periodLabel} />
              <Stat label="Photos" value={totals.photos.toLocaleString()} icon="fa-image" sub={periodLabel} />
              <Stat label="Published" value={totals.published.toLocaleString()} icon="fa-paper-plane" sub={periodLabel} />
              <Stat label="Reports" value={totals.reports.toLocaleString()} icon="fa-file-lines" sub={periodLabel} />
              <Stat label="AI spend" value={formatCost(totals.ai)} icon="fa-robot" sub={periodLabel} />
            </div>

            <div className="ivory-panel sys-panel sys-panel--table">
              <div className="sys-table-scroll">
                <table className="sys-table sys-table--rows">
                  <thead>
                    <tr>
                      {sortable('name', 'Organisation', false)}
                      {sortable('children', 'Children')}
                      <th className="sys-table__num" title="On-roll children posted about in the last 28 days">Covered</th>
                      {sortable('staff', 'Staff')}
                      {sortable('posts', 'Posts')}
                      {sortable('photos', 'Photos')}
                      {sortable('published', 'Published')}
                      {sortable('reports', 'Reports')}
                      {sortable('ai', 'AI spend')}
                      {sortable('activity', 'Last activity', false)}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr><td colSpan={10} className="sys-table__empty">Nothing to show for this period.</td></tr>
                    ) : visible.map(r => {
                      const quietDays = daysSince(r.lastActivityAt);
                      const quiet = quietDays === null || quietDays >= QUIET_DAYS;
                      const trialDays = r.trialEndsAt ? daysSince(r.trialEndsAt) : null;
                      // daysSince is negative for a future date, hence the sign.
                      const trialSoon = trialDays !== null && -trialDays <= TRIAL_SOON_DAYS && -trialDays >= 0;
                      const staleBacklog = (r.backlogOldestDays ?? 0) >= STALE_BACKLOG_DAYS;
                      const coverage = r.children.onRoll > 0
                        ? Math.round((r.children.covered / r.children.onRoll) * 100)
                        : null;
                      const open = () => navigate(`/system-admin/organisations/${r.id}?tab=activity`);
                      return (
                        <tr
                          key={r.id}
                          tabIndex={0}
                          onClick={open}
                          onKeyDown={e => { if (e.key === 'Enter') open(); }}
                          title={`Open ${r.name}`}
                        >
                          <td className="sys-table__name">
                            {r.name}
                            {!r.isActive && <span className="sys-tag">inactive</span>}
                            {trialSoon && (
                              <span className="sys-tag" title={`Trial ends ${r.trialEndsAt}`}>
                                trial ends in {-(trialDays as number)}d
                              </span>
                            )}
                            {staleBacklog && (
                              <span className="sys-tag" title={`${r.unpublishedBacklog} unpublished, oldest ${r.backlogOldestDays} days`}>
                                review backlog
                              </span>
                            )}
                          </td>
                          <td className="sys-table__num" title={`${r.children.archived} archived · ${r.children.leavers} leavers`}>
                            {r.children.onRoll}
                          </td>
                          <td className="sys-table__num sys-table__muted">
                            {coverage === null ? '—' : `${coverage}%`}
                          </td>
                          <td className="sys-table__num">{r.staffTotal}</td>
                          <Delta value={r.posts} average={r.postsAverage} unit={unit} />
                          <Delta value={r.photos} average={r.photosAverage} unit={unit} />
                          <td className="sys-table__num">{r.published.toLocaleString()}</td>
                          <td className="sys-table__num">{r.reports.toLocaleString()}</td>
                          <td className="sys-table__num">{formatCost(r.aiCost)}</td>
                          <td className={quiet ? 'sys-state sys-state--warn' : 'sys-table__muted'}>
                            {quiet && <i className="fas fa-moon" aria-hidden="true" />}
                            {lastActivityLabel(r.lastActivityAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="sys-page__footnote">
              Counts only — no child, staff or post details are available to this page. Weeks run Monday to Sunday,
              UK time. Averages cover the {data?.period.averagePeriods ?? 12} complete {unit === 'month' ? 'months' : 'weeks'} before
              the selected one. “Covered” is a rolling 28 days and does not change with the period.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * A count with its average underneath. The average is the whole point of the
 * column — "18 posts" means nothing until you know whether they normally write
 * 6 or 60.
 */
const Delta: React.FC<{ value: number; average: number; unit: string | null }> = ({ value, average, unit }) => {
  const per = unit === 'month' ? 'month' : 'week';
  return (
    <td className="sys-table__num" title={`Average ${average} per ${per}`}>
      {value.toLocaleString()}
      <span className="sys-delta">{average.toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
    </td>
  );
};

const Stat: React.FC<{ label: string; value: string; icon: string; sub?: string }> = ({ label, value, icon, sub }) => (
  <div className="ivory-panel sys-stat">
    <div className="sys-stat__label">
      <i className={`fas ${icon}`} aria-hidden="true" />
      {label}
    </div>
    <div className="sys-stat__value">{value}</div>
    {sub && <div className="sys-stat__sub">{sub}</div>}
  </div>
);

export default Overview;
